<script lang="ts">
  import BaseStep from "./BaseStep.svelte";
  import SharedHeading from "../SharedHeading.svelte";
  import AddressCard from "../Address/AddressCard.svelte";
  import UserCard from "../User/UserCard.svelte";
  import Button from "../Button.svelte";
  import { activateAndContinueWorkflow } from "../../lib/helpers/activateAndContinue";
  import { onDestroy } from "svelte";
  import { getCopy } from "../../lib/stores/copy";
  import { selectedAddressStore } from "../../lib/stores/addresses";
  import type { PersistedAddress } from "../../lib/api/address";
  import { sessionStore } from "../../lib/stores/session";
  import GiftCard from "../Gift/GiftCard.svelte";
  import CustomHtml from "../CustomHTML.svelte";
  import { startPolling } from "../../lib/helpers/steps/wait";
  import { resetCampaignUserPhase } from "../../lib/helpers/steps/review";
  import LoadingScreen from "../LoadingScreen.svelte";
  import BundleCard from "../Gift/BundleCard.svelte";
  import Youtube from "../Youtube.svelte";
  import { getSearchParam } from "../../lib/helpers/document";
  import MultipleSelectionCard from "../Gift/MultipleSelectionCard.svelte";

  export let currentRoute;

  let isLoading = false;
  let isResettingPhase = false;
  let poller: { stopPolling: boolean } | undefined;

  onDestroy(() => {
    isLoading = false;

    if (poller) {
      poller.stopPolling = true;
    }
  });

  const onConfirmButtonClick = () => {
    isLoading = true;
    void (async () => {
      await activateAndContinueWorkflow();
      poller = startPolling();
    })();
  };

  const onCardEditCallback = async () => {
    isResettingPhase = true;
    await resetCampaignUserPhase();
  };

  $: copy = $getCopy("review") as Record<string, string>;
  $: selectedAddress = $selectedAddressStore as PersistedAddress;
  $: selectedGift = $sessionStore.gift;
  // If it's a fully specified bundle (ssm) we're making it a surprise bundle
  $: bundleItemsChoosen = $sessionStore.socialGiftingMode === "bundle" && getSearchParam('ssm') !== 'true'
      ? $sessionStore.socialGiftBundleSelectedItems
      : undefined;
  $: multipleSelectionItemsChoosen = $sessionStore.multipleSelectionSelectedItems?.length;
  $: hasAddressRoute = !!$sessionStore.routes.find((route) => route.id === "address");
</script>

<BaseStep {currentRoute} showBackButton={false}>
  <svelte:fragment slot="content">
    {#if isResettingPhase}
      <LoadingScreen />
    {:else}
      <div class="w-full flex flex-col gap-4">
        <div class="w-full mx-auto text-center lg:text-left lg:mb-4">
          <SharedHeading headerCopy={copy.headerCopy} />
        </div>

        {#if bundleItemsChoosen}
          <BundleCard hideEditButton={isLoading} />
        {:else if multipleSelectionItemsChoosen}
          <MultipleSelectionCard hideEditButton={isLoading} />
        {:else if selectedGift}
          <GiftCard {selectedGift} hideEditButton={isLoading} />
        {/if}
        <UserCard hideEditButton={isLoading} {onCardEditCallback} />

        {#if hasAddressRoute}
          <AddressCard {selectedAddress} hideEditButton={isLoading} {onCardEditCallback} />
        {/if}

        {#if copy.customHtml}
          <CustomHtml customHtml={copy.customHtml} />
        {/if}

        <!-- Button to send -->
        <Button
          class="w-full md:w-fit lg:mt-6"
          primary
          disabled={isLoading}
          isBusy={isLoading}
          on:click={() => onConfirmButtonClick()}>
          {copy.buttonCopy}
        </Button>
      </div>

      <Youtube videoId={copy.videoId} />
    {/if}
  </svelte:fragment>
</BaseStep>
